<script setup>
/**
 * WHAT THE CUSTOMER'S PHONE SHOWS, one page at a time (services spec P-4.10 design 9).
 *
 * Drawn ONLY from the server's preview (`POST …/tasks/form-preview`): each page's title, the
 * opening words, every field's label as WhatsApp cuts it, the options and the button words are
 * the compiled form's own. A second compiler on this side would be a preview that agrees with
 * the real form until the day it does not. Tapping the button turns the page, as the
 * customer's does; WhatsApp's own back arrow is the customer's way back, so this one wraps.
 */
import { computed, ref, watch } from 'vue';

import { useWasloI18n } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';

const props = defineProps({
  /** The preview answer, `{ pages, fields }`. `null` before the first one arrives. */
  answer: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  failed: { type: Boolean, default: false },
  /** The form's language, `en` or `ar`. */
  lang: { type: String, default: 'en' },
  /** A Both account can look at each language's form. */
  showLanguage: { type: Boolean, default: false },
});

const emit = defineEmits(['update:lang']);

const { t, locale } = useWasloI18n();
const key = name => `WASLO.FORMS.ARRANGE.${name}`;
const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

/** One literal key per language, so the translation checker sees each one. */
const LANGUAGES = [
  { code: 'en', label: 'WASLO.FORMS.LANG.EN' },
  { code: 'ar', label: 'WASLO.FORMS.LANG.AR' },
];

const index = ref(0);
const pages = computed(() => props.answer?.pages ?? []);
watch(
  () => pages.value.length,
  length => {
    if (index.value >= length) index.value = 0;
  }
);
const page = computed(() => pages.value[index.value] ?? null);
const byName = computed(
  () => new Map((props.answer?.fields ?? []).map(field => [field.name, field]))
);
const fieldsOnPage = computed(() =>
  (page.value?.fieldNames ?? [])
    .map(name => byName.value.get(name))
    .filter(Boolean)
);
const pageOf = computed(() =>
  t(key('PAGE_OF'), {
    n: format.value.format(index.value + 1),
    total: format.value.format(pages.value.length),
  })
);

/** Services G2.4: a photo or file question shows WhatsApp's own "add" control. */
const UPLOAD_WORDS = { photo: 'PREVIEW_PHOTOS', file: 'PREVIEW_FILE' };
const uploadWords = field => UPLOAD_WORDS[field.shape] ?? null;

/** The customer's tap: the next page, and the first again after the last. */
const turn = () => {
  index.value = index.value + 1 < pages.value.length ? index.value + 1 : 0;
};
</script>

<template>
  <section class="flex flex-col gap-3" data-waslo-form-phone>
    <div class="flex items-center justify-between gap-2">
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('PREVIEW')) }}
      </p>
      <div v-if="showLanguage" class="flex gap-1" data-waslo-form-phone-langs>
        <button
          v-for="option in LANGUAGES"
          :key="option.code"
          type="button"
          class="px-2 py-1 text-xs rounded-lg"
          :class="
            option.code === lang
              ? 'bg-n-slate-12 text-n-slate-1'
              : 'bg-n-alpha-1 text-n-slate-11'
          "
          :aria-pressed="option.code === lang"
          :data-waslo-form-phone-lang="option.code"
          @click="emit('update:lang', option.code)"
        >
          {{ t(option.label) }}
        </button>
      </div>
    </div>

    <div
      class="flex flex-col w-full max-w-sm gap-3 p-4 rounded-2xl outline outline-1 -outline-offset-1 outline-n-container bg-n-alpha-1"
      :dir="lang === 'ar' ? 'rtl' : 'ltr'"
      data-waslo-form-phone-screen
    >
      <p
        v-if="failed"
        class="mb-0 text-sm text-n-ruby-9"
        data-waslo-form-phone-failed
      >
        {{ t(key('PREVIEW_FAILED')) }}
      </p>
      <p
        v-else-if="!page && loading"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-form-phone-loading
      >
        {{ t(key('PREVIEW_LOADING')) }}
      </p>
      <p
        v-else-if="!page"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-form-phone-empty
      >
        {{ t(key('PREVIEW_EMPTY')) }}
      </p>
      <template v-else>
        <p class="mb-0 text-xs text-n-slate-10" data-waslo-form-phone-page-of>
          {{ pageOf }}
        </p>
        <p
          class="mb-0 text-base font-medium text-n-slate-12"
          dir="auto"
          data-waslo-form-phone-title
        >
          {{ page.title }}
        </p>
        <p
          v-if="page.intro"
          class="mb-0 text-sm text-n-slate-11"
          dir="auto"
          data-waslo-form-phone-intro
        >
          {{ page.intro }}
        </p>
        <div
          v-for="field in fieldsOnPage"
          :key="field.name"
          class="flex flex-col gap-1"
          :data-waslo-form-phone-field="field.name"
        >
          <span class="text-sm text-n-slate-12" dir="auto">
            {{ field.label }}
          </span>
          <span
            v-if="field.labelTruncated"
            class="text-xs text-n-amber-11"
            data-waslo-form-phone-cut
          >
            {{ t(key('CUT_SHORT')) }}
          </span>
          <!-- Field fixes G2.3: ticked boxes, so a square beside each option. -->
          <span
            v-if="field.shape === 'options'"
            class="text-xs text-n-slate-11"
            data-waslo-form-phone-many
          >
            {{ t(key('MANY')) }}
          </span>
          <!-- G1: a live field's choices come from her system when the customer reaches it. -->
          <span
            v-if="field.shape === 'live'"
            class="px-3 py-1 text-sm rounded-lg bg-n-solid-1 text-n-slate-11"
            data-waslo-form-phone-live
          >
            {{ t(key('LIVE_CHOICES')) }}
          </span>
          <span
            v-else-if="uploadWords(field)"
            class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-n-solid-1 text-n-slate-11"
            :data-waslo-form-phone-upload="field.shape"
          >
            <span
              class="shrink-0 size-4"
              :class="
                field.shape === 'photo'
                  ? 'i-lucide-camera'
                  : 'i-lucide-paperclip'
              "
              aria-hidden="true"
            />
            {{ t(key(uploadWords(field))) }}
          </span>
          <ul
            v-else-if="field.options.length"
            class="flex flex-col gap-1 mb-0 list-none"
          >
            <li
              v-for="option in field.options"
              :key="option"
              class="flex items-center gap-2 px-3 py-1 text-sm rounded-lg bg-n-solid-1 text-n-slate-11"
              dir="auto"
            >
              <span
                v-if="field.shape === 'options'"
                class="shrink-0 size-4 i-lucide-square"
                aria-hidden="true"
              />
              <span>{{ option }}</span>
            </li>
          </ul>
          <span
            v-else
            class="block h-8 rounded-lg bg-n-solid-1"
            aria-hidden="true"
          />
        </div>
        <button
          type="button"
          class="w-full px-3 py-2 text-sm font-medium rounded-lg bg-n-slate-12 text-n-slate-1"
          dir="auto"
          data-waslo-form-phone-button
          @click="turn"
        >
          {{ page.button }}
        </button>
      </template>
    </div>
  </section>
</template>
